import { cn } from "@plasma/utils/cn";
import { Check, ChevronDown, Layers, Plus, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

/** Toggle a value in an array - add if not present, remove if present */
function toggleValueInArray<T>(values: T[], valueToToggle: T): T[] {
  if (values.includes(valueToToggle)) {
    return values.filter((v) => v !== valueToToggle);
  }
  return [...values, valueToToggle];
}

/** Determine if all values are empty */
function areAllValuesEmpty<TGroup extends string>(
  groups: readonly TGroup[],
  values: Record<TGroup, unknown[]>
): boolean {
  return groups.every((group) => values[group].length === 0);
}

function isCompleteGroupRecord<TGroup extends string, TValue>(
  groups: readonly TGroup[],
  value: Partial<Record<TGroup, TValue>>
): value is Record<TGroup, TValue> {
  return groups.every((group) => Object.hasOwn(value, group));
}

function buildRecordFromGroups<TGroup extends string, TValue>(
  groups: readonly TGroup[],
  getValue: (group: TGroup) => TValue
): Record<TGroup, TValue> {
  const result: Partial<Record<TGroup, TValue>> = {};
  for (const group of groups) {
    result[group] = getValue(group);
  }
  if (!isCompleteGroupRecord(groups, result)) {
    throw new Error("Failed to build complete group record");
  }
  return result;
}

/** Renders the selected values display based on state */
function SelectedValuesDisplay<TValue extends string, TGroup extends string>({
  groups,
  values,
  areAllSelected,
  allOption,
  placeholder,
  items,
  onValueChange,
}: {
  groups: readonly TGroup[];
  values: Record<TGroup, TValue[]>;
  areAllSelected: boolean;
  allOption?: { selectedLabel?: string };
  placeholder?: string;
  items: Record<TGroup, MultiComboboxItem<TValue>[]>;
  onValueChange: (values: Record<TGroup, TValue[]>) => void;
}) {
  // No values selected - show placeholder
  if (areAllValuesEmpty(groups, values)) {
    return (
      <span className="text-muted-foreground py-1 pl-2">
        {placeholder ?? "Please select"}
      </span>
    );
  }

  // All items selected and we have an allOption label
  if (areAllSelected && allOption) {
    return (
      <span className="text-foreground py-1 pl-2">
        {allOption.selectedLabel}
      </span>
    );
  }

  // Individual items selected - show chips
  return (
    <div className="flex w-full flex-1 flex-wrap gap-2">
      {groups.flatMap((group) => {
        const groupValues = values[group];
        return groupValues.map((value) => {
          const item = items[group].find((i) => i.value === value);
          if (!item) {
            return null;
          }
          return (
            <div
              className="body-sm bg-muted flex items-center gap-1.5 rounded-xl px-2 py-1.5"
              key={value}
            >
              {item.imageUrl && (
                <img
                  alt={`${item.label} logo`}
                  className="size-5 rounded-full"
                  height={20}
                  src={item.imageUrl}
                  width={20}
                />
              )}
              {item.icon && (
                <item.icon className="text-foreground group-disabled:text-muted-foreground size-5" />
              )}
              {item.label}
              <button
                className="text-muted-foreground hover:text-muted-foreground/80 transition-[color,box-shadow]"
                onClick={() => {
                  onValueChange(
                    buildRecordFromGroups(groups, (entryGroup) =>
                      entryGroup === group
                        ? values[entryGroup].filter((val) => val !== value)
                        : values[entryGroup]
                    )
                  );
                }}
                type="button"
              >
                <X className="size-4" />
                <span className="sr-only">Deselect</span>
              </button>
            </div>
          );
        });
      })}
    </div>
  );
}

export interface MultiComboboxItem<TValue extends string> {
  value: TValue;
  label: string;
  imageUrl?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  component?: React.ReactNode;
}

const emptyObject = {};

export const MultiCombobox = <TValue extends string, TGroup extends string>({
  groups,
  items,
  values,
  onValueChange,
  disabled,
  allOption,
  createNewOption,
  placeholder,
  groupLabels = emptyObject,
  noResultsLabel = "No results found",
}: {
  groups: readonly TGroup[];
  items: Record<TGroup, MultiComboboxItem<TValue>[]>;
  values: Record<TGroup, TValue[]>;
  onValueChange: (values: Record<TGroup, TValue[]>) => void;
  disabled?: boolean;
  placeholder?: string;
  allOption?: {
    dropdownLabel?: string;
    selectedLabel?: string;
  };
  createNewOption?: {
    label: string;
    onClick: (name: string) => void;
  };
  groupLabels?: {
    [key in TGroup]?: string;
  };
  noResultsLabel?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const areAllSelected = useMemo(
    () =>
      groups.reduce<number>((acc, group) => acc + values[group].length, 0) ===
      groups.reduce<number>((acc, group) => acc + items[group].length, 0),
    [groups, items, values]
  );

  const [query, setQuery] = useState("");

  const filteredItems = useMemo(
    () =>
      buildRecordFromGroups(groups, (group) =>
        items[group].filter(
          (item) =>
            (areAllSelected || !values[group]?.includes(item.value)) &&
            item.label?.toLowerCase().includes(query.toLowerCase())
        )
      ),
    [areAllSelected, groups, items, query, values]
  );

  const hasFilteredResults = groups.some(
    (group) => filteredItems[group].length > 0
  );

  return (
    <Popover
      onOpenChange={(v) => {
        setIsOpen(disabled ? false : v);
        if (!v) {
          setQuery("");
        }
      }}
      open={isOpen}
    >
      <PopoverTrigger asChild>
        <button
          aria-disabled={disabled}
          aria-expanded={isOpen}
          className={cn(
            "body border-input ring-border hover:bg-background focus-visible:border-ring focus-visible:ring-ring/50 aria-expanded:border-foreground aria-expanded:ring-foreground flex h-10 w-full items-center justify-between rounded-md border bg-transparent py-2 pr-3 pl-2 font-sans shadow-xs transition-[color,box-shadow] focus-visible:ring-[3px] aria-expanded:ring-1",
            "group active:scale-100",
            disabled && "bg-muted text-muted-foreground pointer-events-none"
          )}
          disabled={disabled}
          type="button"
        >
          <div
            aria-expanded={isOpen}
            className="h-fit"
            id="combobox"
            onClick={(e) => {
              if (
                e.target instanceof HTMLElement &&
                e.target.closest("button")
              ) {
                e.preventDefault();
              }
            }}
            onKeyDown={(e) => {
              if (
                (e.key === "Enter" || e.key === " ") &&
                e.target instanceof HTMLElement &&
                e.target.closest("button")
              ) {
                e.preventDefault();
              }
            }}
            role="combobox"
            tabIndex={0}
          >
            <SelectedValuesDisplay
              allOption={allOption}
              areAllSelected={areAllSelected}
              groups={groups}
              items={items}
              onValueChange={onValueChange}
              placeholder={placeholder}
              values={values}
            />
            <ChevronDown className="mb-1.5 ml-2 size-5 shrink-0 self-end" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-130 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            onValueChange={setQuery}
            placeholder="Search..."
            value={query}
          />
          <CommandList>
            {allOption && (
              <CommandGroup className="pb-0">
                <CommandItem
                  aria-selected={areAllSelected}
                  className="items-center gap-2"
                  onSelect={() => {
                    const newValues = areAllSelected
                      ? buildRecordFromGroups(groups, () => [] as TValue[])
                      : buildRecordFromGroups(groups, (group) =>
                          items[group].map((item) => item.value)
                        );

                    onValueChange(newValues);
                    setIsOpen(false);
                  }}
                  value="all"
                >
                  <Layers className="size-5" />
                  {allOption.dropdownLabel}
                  {areAllSelected && (
                    <>
                      <div className="flex-1" />
                      <Check />
                    </>
                  )}
                </CommandItem>
              </CommandGroup>
            )}
            {groups.map((group) => {
              const groupFilteredItems = filteredItems[group];

              return (
                <Fragment key={group}>
                  {(!createNewOption ||
                    (groupFilteredItems.length > 0 && items && values)) && (
                    <CommandGroup
                      className={allOption ? "pt-0" : undefined}
                      heading={groupLabels[group]}
                    >
                      {groupFilteredItems.map((item) => {
                        const isSelected = values[group]?.includes(item.value);
                        return (
                          <CommandItem
                            className="items-center gap-2"
                            key={item.value}
                            onSelect={() => {
                              const newValues = buildRecordFromGroups(
                                groups,
                                (entryGroup) =>
                                  entryGroup === group
                                    ? toggleValueInArray(
                                        values[entryGroup],
                                        item.value
                                      )
                                    : values[entryGroup]
                              );

                              onValueChange(newValues);

                              // Don't auto-close the dropdown
                              setQuery("");
                            }}
                            value={item.value}
                          >
                            {item.component ?? (
                              <>
                                {item.imageUrl && (
                                  <img
                                    alt={`${item.label} logo`}
                                    className="size-5 rounded-full"
                                    height={20}
                                    src={item.imageUrl}
                                    width={20}
                                  />
                                )}
                                {item.icon && (
                                  <item.icon className="text-foreground size-5" />
                                )}
                                {item.label}
                                {isSelected && (
                                  <>
                                    <div className="flex-1" />
                                    <Check />
                                  </>
                                )}
                              </>
                            )}
                          </CommandItem>
                        );
                      })}
                      {!hasFilteredResults && (
                        <div className="text-foreground px-3 py-2">
                          {noResultsLabel}
                        </div>
                      )}
                    </CommandGroup>
                  )}
                  {createNewOption && (
                    <>
                      <CommandSeparator />
                      <CommandGroup>
                        <CommandItem
                          className="text-foreground items-center gap-2"
                          onSelect={() => createNewOption.onClick(query)}
                          value="create-new"
                        >
                          <Plus className="size-5" />
                          <span>
                            {createNewOption.label}
                            {query && `: "${query}"`}
                          </span>
                        </CommandItem>
                      </CommandGroup>
                    </>
                  )}
                </Fragment>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
